// NOTE: Provider class re-exports have been moved to index.ts (web) and
// index.native.ts (RN) to avoid eager resolution of web-only dependencies
// (react-dom) when this module is imported on RN.
import { getConfig, WebSessionManager, ReactNativeSessionManager, getActiveSessionManager, revokeSession } from "@bounded-sh/core";
import { OffchainAuthProvider } from "./providers/offchain-auth-provider";
import { getAuthProviderInstance, getCurrentUser, setAuthProviderInstance, setAuthLoading, setCurrentUser, } from "../global";
import { getPlatform } from "../platform";
import { commitIssuerLogoutBounce } from "./issuer-logout-bounce";
import { getStoredAuthMethod, setStoredAuthMethod, getIssuerSessionKind } from "./stored-auth-method";
import { adoptRebuiltProvider, adoptSessionProvider, clearSessionProviderForLogout, registerAuthRegistrySync } from "./session-provider";
import { isSupportedSolanaRpcNetwork, normalizeSolanaRpcUrl } from "./solana-rpc";
import { ensureSolanaMobileWalletRegistered, } from "./providers/solana-mobile-registration";
// An app's own wallet configuration being contradictory is a developer bug, and
// the SDK keeps it loud rather than quietly dropping the lane. Re-exported so an
// app that catches it can tell it apart from a wallet simply not being there.
export { WalletConfigError } from "./providers/solana-mobile-registration";
let currentAuthProvider = null;
let currentAuthMethod = null;
let currentAuthProviderConfigKey = null;
let initConfig = null;
// Keep this module's private registry inside session-provider.ts's atomic adopt/clear
// blocks. Registered at load so the OIDC/inline paths (oidc-auth.ts), which cannot
// import this module without a cycle, still update it through adoptSessionProvider.
registerAuthRegistrySync((provider, method, configKey) => {
    currentAuthProvider = provider;
    currentAuthMethod = method;
    currentAuthProviderConfigKey = configKey;
});
const objectIdentityKeys = new WeakMap();
let nextObjectIdentityKey = 1;
// --- localStorage helpers: track which auth method created the active session ---
// (implemented in ./stored-auth-method so the wallet providers share ONE writer)
function getStoredIdToken() {
    try {
        return getActiveSessionManager().getIdToken();
    }
    catch (_a) {
        return null;
    }
}
function idTokenIsGuest(idToken, expectedAddress) {
    var _a;
    try {
        if (!idToken)
            return false;
        let b64 = (_a = idToken.split('.')[1]) === null || _a === void 0 ? void 0 : _a.replace(/-/g, '+').replace(/_/g, '/');
        if (!b64)
            return false;
        const pad = b64.length % 4;
        if (pad === 2)
            b64 += '==';
        else if (pad === 3)
            b64 += '=';
        const claims = JSON.parse(getPlatform().atob(b64));
        if ((claims === null || claims === void 0 ? void 0 : claims.is_anonymous) !== true)
            return false;
        if (expectedAddress && (claims === null || claims === void 0 ? void 0 : claims['custom:walletAddress']) !== expectedAddress)
            return false;
        return true;
    }
    catch (_b) {
        return false;
    }
}
function storedSessionIsGuest() {
    return idTokenIsGuest(getStoredIdToken());
}
/**
 * Pick the provider used for an ordinary `init({ appId })` restore.
 *
 * Hosted email remains the default and explicit config always wins. The
 * implicit alternatives are sessions this SDK previously created on this
 * origin: a guest session, or a Solana WALLET session (the widget /
 * loginWithWallet record the 'phantom' marker). Restoring the wallet session
 * as 'email' is what used to WIPE it: the EmailAuthProvider restored the
 * bearer, then clearIncompatibleSession() saw stored 'phantom' !== current
 * 'email' and deleted the session on every reload (LF-005). A stored marker
 * still never OVERRIDES app configuration: explicit `authMethod` wins, and a
 * site-policy hard-off (`walletLogin: false` / `requireEmail: true`) keeps the
 * email restore path, whose incompatible-session clear is the designed outcome.
 */
function authMethodForInit(config) {
    if (config.authMethod != null)
        return config.authMethod;
    const stored = getStoredAuthMethod();
    if (stored === 'guest') {
        if (storedSessionIsGuest())
            return 'guest';
        // A marker is only a restore hint; the bearer is authoritative. Repair a
        // stale marker (for example, a crash/failed switch while a human session
        // was still active) instead of relabelling that human bearer as a guest.
        const hasSession = !!getStoredIdToken();
        setStoredAuthMethod(hasSession ? 'email' : null);
        return 'email';
    }
    if (canonicalAuthMethodOrNull(stored) === 'phantom') {
        if (isWalletLoginDisabled(config) || isEmailRequired(config))
            return 'email';
        if (getStoredIdToken())
            return 'phantom';
        // Wallet marker without a bearer: nothing to restore, repair the marker.
        setStoredAuthMethod(null);
        return 'email';
    }
    return 'email';
}
/**
 * Collapse the wallet-lane spellings onto ONE canonical auth method.
 *
 * 'wallet' and 'mobile-wallet-adapter' both select the same injected/registry
 * wallet path that `loginWithWallet()` and the login widget already record as
 * 'phantom' (INJECTED_AUTH_METHOD) - Solana Mobile is a wallet you pick inside
 * that lane, not a login method of its own, exactly like Solflare or Backpack.
 * Leaving the spellings distinct means the stored marker and `currentAuthMethod`
 * disagree after a widget login, and the next init()'s clearIncompatibleSession
 * would wipe a perfectly good session on every reload. One string, no drift.
 */
function canonicalWalletAuthMethod(authMethod) {
    return authMethod === 'wallet' || authMethod === 'mobile-wallet-adapter'
        ? 'phantom'
        : authMethod;
}
/** Null-tolerant `canonicalWalletAuthMethod`, for comparing a possibly-absent
 *  stored marker against a possibly-absent active method. */
function canonicalAuthMethodOrNull(authMethod) {
    return authMethod === null ? null : canonicalWalletAuthMethod(authMethod);
}
function objectIdentityKey(value) {
    if (!value || (typeof value !== 'object' && typeof value !== 'function'))
        return null;
    const obj = value;
    let id = objectIdentityKeys.get(obj);
    if (!id) {
        id = nextObjectIdentityKey++;
        objectIdentityKeys.set(obj, id);
    }
    return `object:${id}`;
}
function stableConfigValue(value, seen = new WeakSet()) {
    if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
        return value;
    if (typeof value === 'function')
        return '[function]';
    if (Array.isArray(value))
        return value.map((item) => stableConfigValue(item, seen));
    if (typeof value === 'object') {
        if (seen.has(value))
            return '[circular]';
        seen.add(value);
        const out = {};
        for (const key of Object.keys(value).sort()) {
            out[key] = stableConfigValue(value[key], seen);
        }
        seen.delete(value);
        return out;
    }
    return String(value);
}
function authProviderConfigKey(config) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    return JSON.stringify({
        authMethod: (_a = config.authMethod) !== null && _a !== void 0 ? _a : null,
        chain: (_b = config.chain) !== null && _b !== void 0 ? _b : null,
        rpcUrl: normalizeSolanaRpcUrl(config.rpcUrl),
        name: (_c = config.name) !== null && _c !== void 0 ? _c : null,
        logoUrl: (_d = config.logoUrl) !== null && _d !== void 0 ? _d : null,
        phantomConfig: stableConfigValue((_e = config.phantomConfig) !== null && _e !== void 0 ? _e : null),
        walletLogin: stableConfigValue((_f = config.walletLogin) !== null && _f !== void 0 ? _f : null),
        injectedWalletConfig: stableConfigValue((_g = config.injectedWalletConfig) !== null && _g !== void 0 ? _g : null),
        // stableConfigValue flattens every function to "[function]", so a
        // re-init that swaps ONLY a callback would key identically and keep the
        // cached provider - still holding the previous page's closure, waiting
        // on UI that no longer exists. Key the wallet gesture hook by identity.
        confirmWalletAction: objectIdentityKey((_h = resolveInjectedWalletConfig(config).confirmWalletAction) !== null && _h !== void 0 ? _h : null),
        evmWalletConfig: stableConfigValue((_j = config.evmWalletConfig) !== null && _j !== void 0 ? _j : null),
        privyConfig: stableConfigValue((_k = config.privyConfig) !== null && _k !== void 0 ? _k : null),
        mobileWalletConfig: stableConfigValue((_l = config.mobileWalletConfig) !== null && _l !== void 0 ? _l : null),
        privyExpoProvider: objectIdentityKey((_m = config.privyExpoProvider) !== null && _m !== void 0 ? _m : null),
    });
}
/**
 * Clears the session if it was created by a different auth method.
 * Returns true if the session was cleared, false if the session is compatible.
 *
 * Dispatches to the correct session manager based on the current auth method
 * so both web (WebSessionManager) and RN (ReactNativeSessionManager) sessions
 * are properly cleared.
 */
export async function clearIncompatibleSession() {
    // Compare CANONICAL methods. A session minted by an older SDK can carry a
    // legacy wallet spelling ('wallet' / 'mobile-wallet-adapter') while
    // currentAuthMethod is always the canonical 'phantom' (getAuthProvider
    // canonicalizes, and every marker writer stores 'phantom'). A raw string
    // compare then reads that as another provider's session and deletes a
    // perfectly good wallet session on every init - the exact wipe class
    // canonicalWalletAuthMethod exists to close, on the stored side.
    if (canonicalAuthMethodOrNull(getStoredAuthMethod()) === canonicalAuthMethodOrNull(currentAuthMethod)) {
        return false;
    }
    // Clear both managers: on web the RN manager is a no-op (not configured),
    // and on RN the web manager is a no-op (no localStorage).
    //
    // AWAITED. Session removal is serialized through a cross-tab lock now, so a
    // fire-and-forget call would let init() reach setCurrentUser while the old
    // credential was still readable — and a request issued in that gap would
    // authenticate as the session we are in the middle of discarding.
    await WebSessionManager.clearSession();
    try {
        await ReactNativeSessionManager.clearSession();
    }
    catch ( /* not configured on web */_a) { /* not configured on web */ }
    setStoredAuthMethod(null);
    return true;
}
function requireWebPrivyConfig(config) {
    if (!config.privyConfig || typeof config.privyConfig.appId !== 'string' || config.privyConfig.appId.trim() === '') {
        throw new Error('Privy auth requires config.privyConfig.appId; no default Privy app is used.');
    }
    if (!config.privyConfig.config || typeof config.privyConfig.config !== 'object') {
        throw new Error('Privy auth requires config.privyConfig.config for the app-scoped Privy provider.');
    }
    return config.privyConfig;
}
/**
 * The ONE authoritative Solana network for the wallet lane.
 *
 * The wallet-lane override wins over the app-wide chain because that is what
 * actually signs: InjectedWalletProvider resolves `walletLogin.network` /
 * `injectedWalletConfig.network` ahead of the top-level chain for both RPC and
 * the Wallet-Standard chain id. Registration, wallet discovery and the provider
 * must all read this same value - the mobile wallet authorizes per cluster, so
 * two answers means authorizing on one and being asked to sign on the other.
 */
export function getConfiguredSolanaNetwork(config) {
    const chain = typeof config.chain === 'string' ? config.chain.trim() : '';
    const fromChain = chain === '' || chain === 'offchain' ? null : chain;
    const raw = resolveInjectedWalletConfig(config).network;
    const override = typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
    if (!override)
        return fromChain;
    // An override that no Solana surface can resolve must fail HERE, at
    // configuration time. Letting it through registers the mobile wallet on the
    // mainnet default while every later transaction rejects the same value.
    if (!isSupportedSolanaRpcNetwork(override)) {
        throw new Error(`Bounded: wallet login network "${override}" is not supported. ` +
            `Expected one of: solana_devnet, solana_mainnet.`);
    }
    // And it may NAME the app's chain, never contradict it: the wallet would
    // authorize one cluster while the server-side write lane still uses
    // config.chain for its transaction, RPC and reconciliation.
    if (fromChain && fromChain !== override) {
        throw new Error(`Bounded: wallet login network "${override}" contradicts chain "${fromChain}". ` +
            `Both drive Solana signing, so they must match - drop the wallet-login network, ` +
            `or set the chain you actually mean.`);
    }
    return override;
}
// Error thrown when an app tries wallet login WITHOUT opting in at init().
// Wallet login is OFF by default — most apps never support wallets, so the
// wallet choice is only usable when the developer explicitly asked for it.
function walletLoginNotEnabledError(method) {
    return new Error(`Bounded: Solana wallet login (authMethod:"${method}") is OFF by default and not enabled for this app. ` +
        `Wallet login is opt-in — pass \`walletLogin: true\` (or a \`walletLogin\` / \`injectedWalletConfig\` object) ` +
        `to init() to enable the "connect wallet" choice. Most apps don't need it; use hosted login ` +
        `loginWithRedirect(...) for email/social, or signInAnonymously() for zero-friction guests.`);
}
/**
 * Whether the app opted into Solana WALLET LOGIN at init(). OPT-IN, default OFF:
 * enabled only when the developer explicitly passes `walletLogin: true` (or a
 * `walletLogin` / `injectedWalletConfig` object). This gate is what keeps wallet
 * login from being usable (or advertised) by the vast majority of apps that
 * never want it — existing apps that don't opt in see ZERO behavior change.
 */
export function isWalletLoginEnabled(config) {
    const wl = config.walletLogin;
    if (wl === true)
        return true;
    if (wl && typeof wl === 'object')
        return true;
    if (config.injectedWalletConfig && typeof config.injectedWalletConfig === 'object')
        return true;
    return false;
}
/**
 * Whether the app explicitly turned the "connect wallet" lane OFF at init()
 * (`walletLogin: false`). This is the hard opt-OUT the unified widget honors: it
 * hides the wallet option entirely even when an injected wallet is detected and
 * even if a per-call `openBoundedWidget({ wallet: true })` asked for it.
 */
export function isWalletLoginDisabled(config) {
    return config.walletLogin === false;
}
/**
 * Site policy: the app requires an email on file for every user (`requireEmail:
 * true`). Native wallet login carries no email, so the unified widget suppresses
 * the wallet lane when this is set - email/social (which yields an email) remain.
 * This lets a policy deny wallet-only accounts without touching per-call options.
 */
export function isEmailRequired(config) {
    return config.requireEmail === true;
}
/** The config passed to the last `init()`, or null before init. Read-only view
 *  for runtime surfaces (e.g. the unified login widget) that need wallet/rpc
 *  settings without re-plumbing them through every call. */
export function getInitConfig() {
    return initConfig;
}
/**
 * Which LOGIN mode the app runs (default "turnkey"). Turnkey-native email OTP keeps
 * code entry inline in the unified widget (no OIDC popup), and Bounded mints the
 * session from Turnkey's verification. Apps can explicitly select "bounded" for the
 * legacy Better Auth email flow. A per-call openBoundedWidget option can override it.
 */
export function resolveAuthMode(config) {
    return config.authMode === 'bounded' ? 'bounded' : 'turnkey';
}
/**
 * Resolve the injected-wallet-config the provider should use, merging the
 * `walletLogin` object form (if the app passed one) over `injectedWalletConfig`.
 * `walletLogin: true` (bare boolean) enables login with default provider
 * discovery (Phantom-first) and no RPC override.
 */
function resolveInjectedWalletConfig(config) {
    const base = (config.injectedWalletConfig && typeof config.injectedWalletConfig === 'object')
        ? config.injectedWalletConfig
        : {};
    const wl = config.walletLogin;
    if (wl && typeof wl === 'object')
        return Object.assign(Object.assign({}, base), wl);
    return base;
}
function optionalProviderError(method) {
    return new Error(`Bounded authMethod "${method}" uses an optional wallet/provider SDK that is no longer ` +
        `pulled into the default @bounded-sh/client entry. Use hosted login ` +
        `loginWithRedirect(...) for Bounded Auth, signInAnonymously() for guests, ` +
        `or install/import an explicit wallet-provider entry once your app opts into "${method}".`);
}
export { SOLANA_DEVNET_RPC_URL, SOLANA_MAINNET_RPC_URL, } from "./solana-rpc";
export async function getAuthProvider(config) {
    var _a;
    const nextProviderConfigKey = config ? authProviderConfigKey(config) : null;
    if (currentAuthProvider && (!config || currentAuthProviderConfigKey === nextProviderConfigKey)) {
        return currentAuthProvider;
    }
    if (!config) {
        throw new Error("Config is required to initialize auth provider");
    }
    if (currentAuthProvider && currentAuthProviderConfigKey !== nextProviderConfigKey) {
        currentAuthProvider = null;
        currentAuthMethod = null;
        currentAuthProviderConfigKey = null;
    }
    initConfig = config;
    // Keep what the app actually wrote for error text; everything else runs on
    // the canonical method so exactly one string reaches storage and the issuer.
    const requestedAuthMethod = authMethodForInit(config);
    const authMethod = canonicalWalletAuthMethod(requestedAuthMethod);
    const rpcUrl = normalizeSolanaRpcUrl(config.rpcUrl);
    const configuredNetwork = getConfiguredSolanaNetwork(config);
    currentAuthMethod = authMethod;
    // Clear, actionable error when the BROWSER build (`bounded-sh`) is loaded in a
    // non-browser (Node) runtime. The wallet providers below need `window`; without
    // it they throw a cryptic "PhantomWalletProvider can only be instantiated in a
    // browser environment" deep in init(). The server build (`bounded-sh/server`)
    // never resolves these methods (it signs with a keypair), so this can't fire for
    // it — it only converts the cryptic browser-in-Node crash into a useful message.
    // Canonical methods only - the wallet-lane spellings already normalized above.
    const browserWalletMethods = ['phantom', 'evm-wallet', 'rainbowkit', 'coinbase-smart-wallet', 'onboard', 'privy'];
    if (typeof window === 'undefined' && browserWalletMethods.includes(authMethod)) {
        throw new Error(`bounded-sh: '${authMethod}' auth requires a browser, but no \`window\` was found ` +
            `(you're likely running in Node). In server/Node code, import from 'bounded-sh/server' ` +
            `instead of 'bounded-sh' — it signs with an explicit keypair passed to createWalletClient({ keypair }) ` +
            `and needs no browser.`);
    }
    switch (authMethod) {
        case "email":
            // Hosted Bounded Auth sessions restore through the same session
            // manager as legacy email sessions. Human login should be started
            // with loginWithRedirect(); this provider is kept as the restore and
            // logout surface for those sessions.
            currentAuthProvider = new (await import("./providers/email-auth-provider")).EmailAuthProvider();
            break;
        case "phantom": {
            // First-class Solana WALLET LOGIN (the poof/TaroBase-style "connect
            // wallet" choice), and the canonical method every wallet-lane
            // spelling normalizes to. Slim path: rides the injected Solana
            // provider a browser wallet exposes (Phantom's
            // window.phantom.solana, or any Wallet-Standard window.solana). The
            // user's REAL wallet becomes @user.address; login = SIWS; full local
            // signing surface. No React and no heavy wallet-SDK peer dep.
            // Solana Mobile's wallet needs no method of its own: it is
            // registered below as a regular Wallet Standard wallet and then
            // found by the same discovery as every other wallet.
            //
            // OPT-IN, DEFAULT OFF: only selectable by CONFIGURATION when the app
            // explicitly enabled it at init() (walletLogin: true). Otherwise
            // throw a clear, actionable error — most apps never support wallets,
            // so we never construct the provider (or pay its lazy chunk) for
            // them. A RESTORE of a stored wallet session (authMethodForInit) is
            // not configuration: the widget's wallet lane can be enabled
            // per-call or by wallet detection without the init() opt-in, and the
            // session it minted must survive the next reload.
            if (config.authMethod != null && !isWalletLoginEnabled(config)) {
                throw walletLoginNotEnabledError(requestedAuthMethod);
            }
            // Surface the Solana Mobile wallet (Saga/Seeker) on capable devices
            // BEFORE the provider exists, so a session restore followed by a
            // signing call can resolve it from the registry. No-op off-Android.
            // A failure is NOT fatal to init - an app whose users have injected
            // wallets must still start - and is not cached, so the next attempt
            // (login(), a widget open) retries instead of living with a page
            // that permanently has no phone wallet.
            // Registered on a marker RESTORE too, not just an opt-in: a session
            // the widget's wallet lane minted (per-call `wallet: true`, no init
            // opt-in) can belong to the phone wallet, and leaving it
            // unregistered restores a session that then cannot sign - the
            // registry is the only place an MWA wallet is ever found
            // (injected-wallet-provider.resolveProvider). This cannot turn the
            // widget's lane on: `detected` filters Solana Mobile out by name
            // (bounded-login-widget.ts), so registration never counts as
            // evidence the user has a wallet.
            await ensureSolanaMobileWalletRegistered(config, configuredNetwork);
            const { InjectedWalletProvider } = await import("./providers/injected-wallet-provider");
            currentAuthProvider = new InjectedWalletProvider(resolveInjectedWalletConfig(config), rpcUrl, configuredNetwork);
            break;
        }
        case "evm-wallet": {
            // First-class EVM WALLET LOGIN (BYO Ethereum wallet via SIWE). Slim
            // path: rides the EIP-1193 injected provider a browser wallet exposes
            // (MetaMask / Rabby / Coinbase Wallet / any window.ethereum), discovered
            // via EIP-6963. The user's REAL EVM wallet becomes @user.evmAddress;
            // login = SIWE (EIP-4361 personal_sign). No viem/ethers and no React.
            // Selecting authMethod:'evm-wallet' IS the opt-in — pass evmWalletConfig
            // to point at a specific wallet or set chainId/domain.
            const evmWalletConfig = ((_a = config.evmWalletConfig) !== null && _a !== void 0 ? _a : {});
            const { InjectedEvmWalletProvider } = await import("./providers/injected-evm-wallet-provider");
            currentAuthProvider = new InjectedEvmWalletProvider(evmWalletConfig);
            break;
        }
        case "rainbowkit":
            console.warn("Rainbow Kit auth is not yet supported.");
            break;
        case "privy": {
            throw optionalProviderError("privy");
        }
        case "privy-expo":
            // React Native Privy: @privy-io/expo is hook-based, so the host app must
            // create a PrivyExpoProvider, bridge the Privy hooks via setPrivyMethods()
            // from inside its <PrivyProvider> tree, and pass it as config.privyExpoProvider.
            if (!config.privyExpoProvider) {
                throw new Error('authMethod "privy-expo" requires a PrivyExpoProvider instance passed as config.privyExpoProvider. ' +
                    'Create the provider, call setPrivyMethods() from your React Native component tree, then pass it to init().');
            }
            currentAuthProvider = config.privyExpoProvider;
            break;
        case "onboard":
            console.warn("Onboard auth is not yet supported.");
            break;
        case "guest":
            // Anonymous auth: a device-local ed25519 keypair signs the challenge.
            currentAuthProvider = new (await import("./providers/guest-auth-provider")).GuestAuthProvider();
            break;
        default:
            break;
    }
    if (!currentAuthProvider) {
        throw new Error("No auth provider was selected. Does your app have a supported auth method or chain configured?");
    }
    // Wrap with OffchainAuthProvider for offchain/poofnet chain
    if (config.chain === "offchain") {
        console.log("[Offchain] Wrapping auth provider for Poofnet transaction tracking");
        currentAuthProvider = new OffchainAuthProvider(currentAuthProvider);
    }
    currentAuthProviderConfigKey = nextProviderConfigKey;
    return currentAuthProvider;
}
/**
 * Browser/React-Native-only auth flows (guest, email) persist their session via
 * WebSessionManager, which is gated to environments that have `window` /
 * `localStorage`. In Node those stores no-op, so the session is minted on the
 * server but never saved locally — the caller looks logged in yet every request
 * 403s. Fail fast with actionable guidance instead of that silent footgun.
 */
function assertBrowserAuthEnv(method) {
    if (typeof window === 'undefined') {
        throw new Error(`bounded-sh: '${method}' is a browser/React-Native auth flow — it needs \`window\`/\`localStorage\` ` +
            `to persist the session, which Node doesn't have (the session would be silently dropped, then every ` +
            `request 403s). In Node/server code import from 'bounded-sh/server' and sign with a keypair ` +
            `by using createWalletClient({ keypair }).`);
    }
}
/**
 * Anonymous ("guest") sign-in: zero-friction auth backed by a device-local
 * ed25519 keypair (no email, no wallet, no popup). The keypair is generated +
 * persisted on the device; its public key becomes `@user.address`. Durable
 * across reloads. Upgrade later by linking an email; transfer accounts via the
 * ownership-as-data pattern. `init()` must have run first. Browser/RN only.
 */
export async function signInAnonymously() {
    var _a;
    assertBrowserAuthEnv('signInAnonymously');
    // Resolve configuration before taking the transaction snapshot. Although
    // getConfig is normally already resolved after init(), it is asynchronous;
    // another login that finishes during that await must be part of the prior
    // state we preserve, not accidentally split across old/new snapshots.
    const coreConfig = await getConfig();
    const previousAuthProvider = currentAuthProvider;
    const previousAuthMethod = currentAuthMethod;
    const previousProviderConfigKey = currentAuthProviderConfigKey;
    const previousStoredAuthMethod = getStoredAuthMethod();
    const previousGlobalProvider = getAuthProviderInstance();
    const previousUser = getCurrentUser();
    const previousCoreProvider = coreConfig.authProvider;
    const previousCoreAuthMethod = coreConfig.authMethod;
    const previousIdToken = getStoredIdToken();
    // Lazy-load the guest provider so its ed25519/web3 dependencies stay out of
    // the main bundle for data-only apps. Specifier is a static string literal
    // so bundle-splitting can see it.
    const provider = new (await import("./providers/guest-auth-provider")).GuestAuthProvider();
    setAuthLoading(true); // reflect "signing in…" while the guest keypair session is minted
    // adoptSessionProvider awaits only getConfig() (already resolved here) and then
    // runs every write - provider/method/config/marker/user - in one synchronous
    // block, so listeners and concurrent imperative calls can never observe a
    // mixed identity.
    const commit = (user) => adoptSessionProvider({ provider, method: 'guest', configKey: 'direct:guest', user });
    try {
        // Mint/store first while every shared provider surface remains on the
        // previous identity. The provider also withholds setCurrentUser here.
        const user = await provider.loginForProviderSwitch();
        if (!user)
            return null;
        // A human login may have completed after the guest request persisted but
        // before its promise resumed here. Re-check the live bearer immediately
        // before commit so a late guest result cannot overwrite that newer user.
        if (!user.address || !idTokenIsGuest(getStoredIdToken(), user.address)) {
            throw new Error('Anonymous sign-in was superseded by another session.');
        }
        await commit(user);
        return user;
    }
    catch (error) {
        const currentIdToken = getStoredIdToken();
        if (currentIdToken !== previousIdToken && idTokenIsGuest(currentIdToken)) {
            // Defensive applied-yet-error reconciliation: if storage contains a
            // valid guest bearer/key despite the thrown write, commit that truth
            // instead of restoring a human UI over a guest token.
            let recovered = null;
            try {
                recovered = await provider.restoreSessionForProviderSwitch();
            }
            catch (_b) {
                try {
                    await getActiveSessionManager().clearSession();
                }
                catch ( /* fail closed below */_c) { /* fail closed below */ }
            }
            if (recovered) {
                await commit(recovered);
                return recovered;
            }
            // restoreSessionForProviderSwitch fails closed and clears a split
            // bearer. Do not resurrect a user object that no longer has its token.
            setCurrentUser(null);
        }
        else if (currentIdToken === previousIdToken) {
            // The staged attempt never changed the bearer; every shared provider
            // surface stayed untouched, so restore the exact prior user/marker.
            currentAuthProvider = previousAuthProvider;
            currentAuthMethod = previousAuthMethod;
            currentAuthProviderConfigKey = previousProviderConfigKey;
            coreConfig.authProvider = previousCoreProvider;
            coreConfig.authMethod = previousCoreAuthMethod;
            setAuthProviderInstance(previousGlobalProvider);
            setStoredAuthMethod(previousStoredAuthMethod);
            if (getCurrentUser() !== previousUser)
                setCurrentUser(previousUser);
        }
        else {
            // A different non-guest token won while this staged guest mint was in
            // flight (another tab/popup/login). The guest attempt never mutated
            // shared provider state, so preserve that winner exactly. Align only
            // this module's private registry to the provider the winner installed;
            // never overwrite its core/global provider, marker, or user snapshot.
            const concurrentProvider = (_a = getAuthProviderInstance()) !== null && _a !== void 0 ? _a : coreConfig.authProvider;
            if (concurrentProvider)
                currentAuthProvider = concurrentProvider;
            const concurrentStoredMethod = getStoredAuthMethod();
            currentAuthMethod = concurrentStoredMethod && concurrentStoredMethod !== 'guest'
                ? concurrentStoredMethod
                : (previousAuthMethod && previousAuthMethod !== 'guest' ? previousAuthMethod : 'email');
            currentAuthProviderConfigKey = previousProviderConfigKey;
        }
        throw error;
    }
    finally {
        setAuthLoading(false);
    }
}
/**
 * Log in with Privy as a co-equal route — selectable at runtime alongside
 * `login()` (email/wallet) and `signInAnonymously()`, without re-`init()`.
 *
 * On REACT NATIVE the host must have created a PrivyExpoProvider and bridged the
 * Privy hooks via setPrivyMethods() (pass it as `config.privyExpoProvider` to
 * init()) — RN can't construct it here because the Privy hooks live in the app's
 * component tree. Web Privy is no longer loaded through the default client entry;
 * opt into a dedicated provider entry when your app needs it.
 *
 * Either way the Privy Solana wallet signs the SIWS challenge, so the session is
 * minted via the same wallet path as Phantom and shares the unified session store
 * (web localStorage / RN storage) and identity model. `init()` must have run first.
 */
export async function loginWithPrivy(options) {
    assertBrowserAuthEnv('loginWithPrivy');
    const coreConfig = await getConfig();
    let provider;
    if (getPlatform().hasDOM) {
        throw optionalProviderError("privy");
    }
    if (!coreConfig.privyExpoProvider) {
        throw new Error('loginWithPrivy() on React Native requires a PrivyExpoProvider bridged from your app: ' +
            'create it, call setPrivyMethods() inside <PrivyProvider>, and pass it as config.privyExpoProvider to init().');
    }
    provider = coreConfig.privyExpoProvider;
    const authMethod = 'privy-expo';
    if (coreConfig.chain === 'offchain') {
        provider = new OffchainAuthProvider(provider);
    }
    // Snapshot every provider-registry surface BEFORE touching any of them, then
    // commit only on a real user - mirroring signInAnonymously(). A cancelled or
    // failed Privy login (provider.login() returns null or throws) must never
    // leave signing/logout/restore routed to Privy while the live bearer session
    // still belongs to the previous provider. Staging nothing up front means the
    // restore is a defensive no-op today and stays correct if login() ever
    // mutates shared state.
    const previousAuthProvider = currentAuthProvider;
    const previousAuthMethod = currentAuthMethod;
    const previousProviderConfigKey = currentAuthProviderConfigKey;
    const previousStoredAuthMethod = getStoredAuthMethod();
    const previousGlobalProvider = getAuthProviderInstance();
    const previousCoreProvider = coreConfig.authProvider;
    const previousCoreAuthMethod = coreConfig.authMethod;
    const restorePreviousProvider = () => {
        currentAuthProvider = previousAuthProvider;
        currentAuthMethod = previousAuthMethod;
        currentAuthProviderConfigKey = previousProviderConfigKey;
        coreConfig.authProvider = previousCoreProvider;
        coreConfig.authMethod = previousCoreAuthMethod;
        setAuthProviderInstance(previousGlobalProvider);
        setStoredAuthMethod(previousStoredAuthMethod);
    };
    if (typeof provider.setLoginOverrides === 'function') {
        provider.setLoginOverrides(options !== null && options !== void 0 ? options : null);
    }
    let result;
    try {
        result = await provider.login();
    }
    catch (error) {
        restorePreviousProvider();
        throw error;
    }
    if (!result) {
        restorePreviousProvider();
        return null;
    }
    // Commit the Privy provider only now that login() returned a real user, so
    // no observer can see a mixed provider/method/config/global/marker state.
    await adoptSessionProvider({
        provider,
        method: authMethod,
        configKey: authProviderConfigKey(Object.assign(Object.assign({}, coreConfig), { authMethod: authMethod })),
    });
    return Object.assign(Object.assign({}, result), { provider });
}
// The SHARED half of readiness: resolved config and the wallet-provider chunk.
// Cached on success and forever - a loaded chunk does not unload - and dropped
// on failure so it can be retried. Deliberately NOT keyed by config: neither
// piece depends on which Solana network the app chose.
let sharedWalletReady = null;
/**
 * Prepare the work EVERY wallet login needs, whichever wallet is used: resolved
 * configuration and the code-split wallet provider. Enough on its own when the
 * caller already knows which wallet it wants.
 *
 * Rejects if that work fails, which means there is no wallet login at all.
 */
export function ensureSharedWalletLoginReady() {
    if (!sharedWalletReady) {
        sharedWalletReady = (async () => {
            await getConfig();
            await import("./providers/injected-wallet-provider");
        })().catch((error) => {
            sharedWalletReady = null;
            throw error;
        });
    }
    return sharedWalletReady;
}
/**
 * Prepare everything a wallet login needs, and resolve only when it is ready.
 *
 * `loginWithWallet` resolves config, code-splits the provider and registers the
 * mobile wallet. On a cold page that work would otherwise land between the
 * user's tap and the wallet handoff - the pre-connect delay that costs Chrome's
 * transient activation and makes the mobile wallet's intent navigation fail. So
 * call this when your wallet control becomes visible and AWAIT it before
 * enabling that control.
 *
 * Must be called after `init()`: the mobile wallet is registered for the app's
 * configured Solana network, and that registration cannot be withdrawn.
 *
 * REJECTS only when the SHARED work fails - there is then no wallet login at
 * all. A mobile-wallet failure instead resolves with `mobileWallet: 'failed'`,
 * because every injected wallet still works and a caller that offers those
 * should carry on without the phone wallet. The mobile half keeps its own
 * per-configuration memo, so a failure there stays retryable and a re-init for
 * another network is never served the previous registration.
 */
export async function ensureWalletLoginReady() {
    const cfg = initConfig;
    if (!cfg) {
        throw new Error('Bounded: the wallet lane must be prepared after init() - the mobile wallet is ' +
            'registered for the app\'s configured Solana network, and that registration ' +
            'cannot be changed afterwards.');
    }
    await ensureSharedWalletLoginReady();
    const mobileWallet = await ensureSolanaMobileWalletRegistered(cfg, getConfiguredSolanaNetwork(cfg));
    return { mobileWallet };
}
/** @deprecated Use {@link ensureWalletLoginReady}, which reports what is ready. */
export function preloadWalletLogin() {
    return ensureWalletLoginReady();
}
/**
 * Log in with an injected Solana WALLET (Phantom / Solflare / Backpack / any
 * Wallet-Standard `window.solana`) as a co-equal RUNTIME route - selectable
 * alongside `login()` / hosted `loginWithPopup()` / `signInAnonymously()` WITHOUT
 * re-`init()`. This is the "Continue with wallet" choice the unified Bounded
 * login widget offers: it NEVER touches Better Auth - the user's real wallet
 * becomes `@user.address` and login is a SIWS signature.
 *
 * `getProvider` pins a specific discovered wallet (e.g. when the widget lists
 * several); omit it to use Phantom-first discovery. Must be called from a user
 * gesture so the wallet's connect prompt is allowed. `init()` must have run first.
 */
export async function loginWithWallet(options) {
    var _a, _b;
    assertBrowserAuthEnv('loginWithWallet');
    const coreConfig = await getConfig();
    const cfg = initConfig !== null && initConfig !== void 0 ? initConfig : {};
    const base = resolveInjectedWalletConfig(cfg);
    const confirmSignIn = options === null || options === void 0 ? void 0 : options.confirmSignIn;
    const injectedConfig = Object.assign(Object.assign(Object.assign({}, base), ((options === null || options === void 0 ? void 0 : options.getProvider) ? { getProvider: options.getProvider } : {})), (confirmSignIn
        ? {
            confirmWalletAction: (action) => {
                var _a, _b;
                return (action === 'login'
                    ? confirmSignIn()
                    : (_b = (_a = base.confirmWalletAction) === null || _a === void 0 ? void 0 : _a.call(base, action)) !== null && _b !== void 0 ? _b : Promise.resolve());
            },
        }
        : {}));
    const rpcUrl = normalizeSolanaRpcUrl(cfg.rpcUrl);
    const network = getConfiguredSolanaNetwork(cfg);
    // Resolve the configured getter ONCE and log in with exactly what it
    // returned. It is app code with no idempotence contract, so asking it twice
    // - once to decide what to prepare, once to actually connect - lets the two
    // answers disagree: a getter that yields a wallet and then nothing prepares
    // for a pinned wallet and then logs in with whatever discovery finds, and
    // one that yields nothing and then a wallet prepares the mobile registry
    // after the tap for a wallet it never uses.
    const pinnedProvider = (_b = (_a = injectedConfig.getProvider) === null || _a === void 0 ? void 0 : _a.call(injectedConfig)) !== null && _b !== void 0 ? _b : null;
    const attemptConfig = Object.assign(Object.assign({}, injectedConfig), { 
        // Still a fallthrough when it resolved nothing, exactly as before - the
        // difference is that the answer is now fixed for this attempt.
        getProvider: () => pinnedProvider });
    // Idempotent: a caller that prepared the lane (the widget, or an app that
    // awaited ensureWalletLoginReady before enabling its button) has already
    // paid for this, so nothing activation-sensitive happens after the tap. A
    // caller that did not gets it here - late, but correct. A SHARED failure is
    // fatal and propagates; there is no wallet login without it.
    //
    // "Pinned" is about the EFFECTIVE resolution, not whether a getter was
    // passed: a configured walletLogin.getProvider pins just as much as a
    // per-call one, and a getter that returns nothing falls through to discovery
    // (and so to the mobile wallet). With a real wallet in hand only the shared
    // half is needed - re-attempting a failed mobile registration would be
    // exactly the work-after-the-tap this exists to avoid, for a wallet this
    // login is not even using.
    if (pinnedProvider)
        await ensureSharedWalletLoginReady();
    else
        await ensureWalletLoginReady();
    const { InjectedWalletProvider } = await import("./providers/injected-wallet-provider");
    // Keep the RAW wallet provider: the offchain wrapper forwards only a fixed
    // set of methods, so cleanup below must speak to the provider that actually
    // holds the config rather than to whatever is wrapping it.
    const walletProvider = new InjectedWalletProvider(attemptConfig, rpcUrl, network);
    let provider = walletProvider;
    if (coreConfig.chain === 'offchain')
        provider = new OffchainAuthProvider(provider);
    // The provider stores the session + user + auth-method marker itself on success.
    let user;
    try {
        user = await provider.login();
    }
    finally {
        // The per-call gesture is a ONE-SHOT closure over this caller's UI (the
        // widget's view, a button). This provider outlives the attempt once it
        // is committed as the active one, so leaving the hook installed would
        // make a LATER login wait on a dismissed modal or a detached button that
        // can never resolve. Drop it on every exit and fall back to whatever
        // init() configured.
        if (confirmSignIn) {
            walletProvider.setWalletConfig(Object.assign(Object.assign({}, attemptConfig), { confirmWalletAction: base.confirmWalletAction }));
        }
    }
    if (!user)
        return null;
    await adoptSessionProvider({
        provider,
        method: 'phantom',
        configKey: authProviderConfigKey(Object.assign(Object.assign({}, cfg), { authMethod: 'phantom' })),
    });
    return Object.assign(Object.assign({}, user), { provider });
}
export async function login(options) {
    // Re-initialize provider if it was cleared by logout
    if (!currentAuthProvider && initConfig) {
        currentAuthProvider = await getAuthProvider(initConfig);
        await adoptRebuiltProvider(currentAuthProvider, currentAuthMethod);
    }
    if (!currentAuthProvider) {
        throw new Error("Auth provider not initialized. Please call init() first.");
    }
    // login() runs inside the app's click handler, so only ALREADY-PREPARED work
    // is safe here: retrying the mobile registration would spend the activation
    // the wallet handoff needs. The shared half is a no-op once prepared, and
    // the honest failure for an unprepared page is the provider's own "no wallet
    // found", which names ensureWalletLoginReady().
    if (currentAuthMethod === 'phantom')
        await ensureSharedWalletLoginReady();
    // Forward per-call overrides to providers that support them (duck-typed to stay
    // lazy-load safe — no direct import of PhantomWalletProvider here).
    // Always call (even with null) so previous overrides are cleared when this
    // login() is invoked without options — early-return paths inside the provider
    // bypass the .finally() cleanup, so we reset here too.
    if (typeof currentAuthProvider.setLoginOverrides === 'function') {
        currentAuthProvider.setLoginOverrides(options !== null && options !== void 0 ? options : null);
    }
    const loginResult = await currentAuthProvider.login();
    if (loginResult) {
        // Store which auth method was used so we restore the right provider on reload
        setStoredAuthMethod(currentAuthMethod);
        // If this top-level tab was opened solely to perform the wallet connect for
        // an embedded preview (?poofConnect — see openTopLevelForConnect in the
        // phantom provider), close it now that the session is established. The
        // session reflects back into the preview's iframe via same-origin storage.
        try {
            if (typeof window !== 'undefined'
                && window.top === window.self
                && new URLSearchParams(window.location.search).get('poofConnect') === '1') {
                setTimeout(() => { try {
                    window.close();
                }
                catch ( /* noop */_a) { /* noop */ } }, 400);
            }
        }
        catch ( /* noop */_a) { /* noop */ }
        return Object.assign(Object.assign({}, loginResult), { provider: currentAuthProvider });
    }
    return null;
}
export function getCurrentAuthMethod() {
    return currentAuthMethod;
}
// The hosted issuer keeps its OWN browser session (the Better Auth cookie on
// auth.bounded.sh) — clearing the app-local session alone leaves it alive, so
// the next loginWithRedirect() silently re-signs the user in as the SAME
// account: "sign out" that never lets you re-choose. The issuer ships
// GET /logout for exactly this — it expires the session cookies and bounces
// back. #228 replaced the issuer's old hostname allowlist with a live-session
// identity match + originAllowedForApp (which a mapped custom domain satisfies),
// so the client no longer mirrors a host list: end the issuer session with a
// top-level bounce on ANY https (or loopback) context and let the issuer decide
// the return. A hostile origin still gets the user signed out and its return
// refused there. Only genuinely un-bounceable contexts (no window, framed, or a
// non-loopback http origin the issuer would reject anyway) stay local-only.
function issuerLogoutBounceUrl(issuerBase, idTokenHint) {
    if (typeof window === 'undefined' || typeof document === 'undefined')
        return null; // RN/Node: no shared cookie jar to clear this way
    try {
        if (window.top !== window.self)
            return null; // framed: SameSite=Lax cookies don't ride iframe navigations
    }
    catch (_a) {
        return null;
    }
    try {
        const here = new URL(window.location.href);
        const host = here.hostname;
        const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host.endsWith('.localhost');
        // HIGH-003: no hostname allowlist - it was a stale mirror of a server rule #228
        // deleted (the suffix match was itself a vuln). Bounce on any top-level https (or
        // loopback) origin, including mapped custom domains; the issuer's originAllowedForApp
        // binding decides the return authoritatively on the next hop. Off-https non-loopback
        // origins the issuer rejects anyway, so they stay local-only.
        const trusted = here.protocol === 'https:' || isLocalhost;
        if (!trusted)
            return null;
        const base = issuerBase.replace(/\/$/, '');
        // #228: send the id_token_hint so the issuer can bind the post-logout redirect to this
        // session + app + registered origin. Without it the issuer safely falls back to its own
        // origin (it no longer trusts a bare suffix match), so the return-to-app bounce is a no-op.
        const hint = idTokenHint ? `&id_token_hint=${encodeURIComponent(idTokenHint)}` : '';
        return `${base}/logout?redirect_uri=${encodeURIComponent(here.href)}${hint}`;
    }
    catch (_b) {
        return null;
    }
}
// #228 helper, deliberately OUTSIDE logout(): the logout body is source-guarded
// against any `catch` so a revokeSession failure can never be swallowed, and a
// missing id token is the one benign absence worth tolerating here.
function idTokenHintOrNull(sm) {
    try {
        return sm.getIdToken();
    }
    catch (_a) {
        return null; /* no id token to hint with */
    }
}
// Also OUTSIDE logout() for the same source-guard reason: destroying the device's 24h
// wallet signing session is best-effort BY CONTRACT (it needs a hidden signer iframe,
// which can be blocked or offline), so its failures are the one thing logout may
// tolerate - never revokeSession's. Lazy import per this module's rule (the signer
// bridge is browser-only machinery). Never rejects.
async function sweepTurnkeySigningSession() {
    try {
        await (await import("./turnkey-signer-bridge")).clearTurnkeySigningSession();
    }
    catch ( /* best-effort: never block or fail logout on the sweep */_a) { /* best-effort: never block or fail logout on the sweep */ }
}
export async function logout(options) {
    // Re-initialize the provider if it was cleared by a prior logout — the exact mirror
    // of login()'s self-heal above. Every session-establishment path now adopts through
    // session-provider.ts, so this is a REPAIR (the registry was cleared by a previous
    // logout), not a missed establishment: without it, logging out a session established
    // after that logout would throw "not initialized" at the user. Rebuilding from
    // initConfig is idempotent and cheap (same config key), and deliberately does not
    // touch the stored marker - see adoptRebuiltProvider.
    if (!currentAuthProvider && initConfig) {
        currentAuthProvider = await getAuthProvider(initConfig);
        await adoptRebuiltProvider(currentAuthProvider, currentAuthMethod);
    }
    if (!currentAuthProvider) {
        throw new Error("Auth provider not initialized. Please call init() first.");
    }
    // Revoke the refresh-token family server-side BEFORE the provider clears the
    // local session (so we still have the token + issuer to revoke). If revoke
    // fails, surface the error and leave local state intact so callers do not
    // mistake a local-only logout for server revocation.
    const sm = getActiveSessionManager();
    const refreshToken = sm.getRefreshToken();
    const sessionIssuer = sm.getIssuer();
    // #228: capture the id token NOW, before currentAuthProvider.logout() clears the session, so we
    // can hand it to the issuer /logout as an id_token_hint (which binds the post-logout redirect).
    const idTokenHint = idTokenHintOrNull(sm);
    if (refreshToken)
        await revokeSession(refreshToken, sessionIssuer !== null && sessionIssuer !== void 0 ? sessionIssuer : undefined);
    // Logout must DESTROY the device's 24h wallet signing session, not leave it to
    // expire (TURNKEY-EMAIL-SIGNING-PLAN.md). It lives on the signer origin, so the
    // sweep is a hidden signer-iframe exchange; start it now (after revocation is
    // committed - a failed logout leaves the still-logged-in user their session) and
    // let it overlap the provider teardown. sweepTurnkeySigningSession never rejects.
    const signingSessionSweep = sweepTurnkeySigningSession();
    // Only OIDC-hosted sessions own an issuer browser cookie. Turnkey-native
    // email OTP is also an email auth method, but it mints the app session
    // inline and has no issuer cookie to clear. Use the explicit session-kind
    // marker when present. The auth-method fallback preserves correct logout
    // behavior for hosted sessions created by older SDK versions.
    const issuerSessionKind = getIssuerSessionKind();
    const wasHosted = issuerSessionKind === 'hosted'
        || (issuerSessionKind === null
            && (currentAuthMethod === 'email' || getStoredAuthMethod() === 'email'));
    await currentAuthProvider.logout();
    // Clear EVERY session-ownership pointer, including core's signing pointer
    // (getConfig().authProvider). Leaving the core pointer on the dead provider is
    // what wedged wallet -> email switches: the next inline email login only set the
    // singleton, so onchain writes still signed through the logged-out wallet
    // provider and its DS2-0369 account guard threw on every set().
    await clearSessionProviderForLogout();
    // Hold logout open until the signing-session sweep settles (it is bounded by its
    // own short timeout), so a hosted bounce navigation cannot cancel it mid-flight.
    await signingSessionSweep;
    if (wasHosted && !(options === null || options === void 0 ? void 0 : options.keepIssuerSession)) {
        // wasHosted is read from GLOBAL storage markers, but the bounce destination is
        // THIS session's OWN issuer. A mixed login history (hosted email login, then a
        // wallet login) leaves the markers saying "hosted" while the live session was
        // minted by the WALLET issuer - which ships no /logout route, so the reader
        // lands on a 404 instead of back in the app. Bounce only when the session's
        // issuer IS the hosted one. A skipped bounce costs nothing: the refresh-token
        // family is already revoked above, and a wallet session owns no issuer cookie.
        const hostedIssuer = (await getConfig()).humanAuthApiUrl || 'https://auth.bounded.sh';
        const trimSlash = (url) => url.replace(/\/+$/, '');
        if (sessionIssuer && trimSlash(sessionIssuer) !== trimSlash(hostedIssuer))
            return;
        const bounce = issuerLogoutBounceUrl(hostedIssuer, idTokenHint);
        // Local state is already cleared, so even if the navigation is stopped
        // (tab close, extension) the app-side logout has fully happened. Hold
        // resolution until the bounce COMMITS: resolving right after queueing it
        // let callers who navigate next (`await logout(); location.reload()`)
        // cancel the pending bounce, leaving the issuer session alive to be
        // silently SSO-ed back in on the next login.
        if (bounce)
            await commitIssuerLogoutBounce(bounce);
    }
}
// OIDC Authorization Code + PKCE login (the secure multi-tenant redirect flow).
export { loginWithRedirect, completeLoginFromRedirect, loginWithPopup, completeLoginInPopup } from './oidc-auth';
